import React, { useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarCheck, CalendarClock, ListChecks, Info, ChevronDown, CheckCircle, Home, Wrench, Users, Building2, FileSpreadsheet, Download, UploadCloud, Calendar, Layers, Zap, Wallet, ShieldCheck, MapPin, Link2, ArrowLeft, ArrowRight } from 'lucide-react';
import { useScreenInit } from '../useScreenInit.js';
type Role = 'host' | 'cleaner' | 'manutentore';
type Step = 'role' | 'account' | 'proprieta' | 'canali' | 'zona' | 'servizi' | 'cleaner' | 'team' | 'clienti' | 'pianificati' | 'opportunita' | 'done';
// Per-role ordered step machine (excluding the shared 'role' entry screen).
// Benefits are no longer a standalone step — they live on the final 'done' screen.
const ROLE_STEPS: Record<Role, Step[]> = {
  host: ['account', 'proprieta', 'canali', 'done'],
  cleaner: ['account', 'servizi', 'cleaner', 'clienti', 'pianificati', 'done'],
  manutentore: ['account', 'servizi', 'team', 'clienti', 'pianificati', 'done']
};
const ROLE_ROUTES: Record<Role, string> = {
  host: '/dashboard',
  cleaner: '/cm',
  manutentore: '/man'
};
interface BenefitItem {
  icon: React.ReactNode;
  title: string;
  desc: string;
}
const ROLE_BENEFITS: Record<Role, BenefitItem[]> = {
  host: [{
    icon: <CalendarCheck size={20} className="text-coral" data-id="element-3383" />,
    title: 'Pulizie e manutenzione gestite',
    desc: 'Turnover pianificati in automatico dai check-in/out: nessun coordinamento manuale.'
  }, {
    icon: <ShieldCheck size={20} className="text-coral" data-id="element-3384" />,
    title: 'Professionisti suggeriti',
    desc: 'Cleaner e manutentori verificati in zona, proposti in base alle tue esigenze.'
  }, {
    icon: <Layers size={20} className="text-coral" data-id="element-3385" />,
    title: 'Interventi aggregati, più risparmio',
    desc: 'Unisciti ad altri host vicini sullo stesso intervento e dividi i costi.'
  }, {
    icon: <Zap size={20} className="text-coral" data-id="element-3386" />,
    title: 'Interventi urgenti',
    desc: 'Segnala un problema e ricevi risposte rapide dai professionisti disponibili.'
  }],
  cleaner: [{
    icon: <Calendar size={20} className="text-coral" data-id="element-3387" />,
    title: 'Incarichi in zona',
    desc: 'Ricevi proposte di pulizie turnover vicino a dove già operi.'
  }, {
    icon: <Layers size={20} className="text-coral" data-id="element-3388" />,
    title: 'Gruppi e route ottimizzate',
    desc: 'Aggrega più pulizie nella stessa zona e ottimizza i tuoi spostamenti.'
  }, {
    icon: <Users size={20} className="text-coral" data-id="element-3389" />,
    title: 'Gestisci il tuo team',
    desc: 'Assegna pulizie ai tuoi cleaner e monitora la qualità in un solo posto.'
  }, {
    icon: <Wallet size={20} className="text-coral" data-id="element-3390" />,
    title: 'Guadagni trasparenti',
    desc: 'Compensi chiari per ogni incarico, pagamenti tracciati.'
  }],
  manutentore: [{
    icon: <MapPin size={20} className="text-coral" data-id="element-3391" />,
    title: 'Opportunità vicine a te',
    desc: 'Interventi suggeriti in base a zona, competenza ed efficienza di spostamento.'
  }, {
    icon: <Layers size={20} className="text-coral" data-id="element-3392" />,
    title: 'Interventi aggregati',
    desc: 'Candidati a gruppi di host che richiedono lo stesso lavoro: più lavoro, meno viaggi.'
  }, {
    icon: <Zap size={20} className="text-coral" data-id="element-3393" />,
    title: 'Richieste urgenti',
    desc: 'Rispondi per primo alle urgenze in zona e aumenta le assegnazioni.'
  }, {
    icon: <Wallet size={20} className="text-coral" data-id="element-3394" />,
    title: 'Guadagni trasparenti',
    desc: 'Prezzi suggeriti per intervento e compensi tracciati.'
  }]
};
const ROLE_OPTIONS: {
  role: Role;
  label: string;
  desc: string;
  icon: React.ReactNode;
}[] = [{
  role: 'host',
  label: 'Host',
  desc: 'Gestisci pulizie e manutenzione delle tue proprietà.',
  icon: <Home size={22} data-id="element-3395" />
}, {
  role: 'cleaner',
  label: 'Cleaner Manager',
  desc: 'Coordina pulizie e team di addetti in zona.',
  icon: <Users size={22} data-id="element-3396" />
}, {
  role: 'manutentore',
  label: 'Manutentore',
  desc: 'Trova interventi e opportunità vicino a te.',
  icon: <Wrench size={22} data-id="element-3397" />
}];
const ROLE_TRADES: Record<'cleaner' | 'manutentore', string[]> = {
  cleaner: ['Pulizie turnover', 'Check-in / Check-out', 'Pulizie ordinarie', 'Deep cleaning', 'Sanificazione', 'Igienizzazione bagni', 'Cambio biancheria', 'Lavanderia & stireria', 'Rifacimento letti', 'Pulizia cucina & elettrodomestici', 'Pulizia vetri & infissi', 'Trattamento pavimenti', 'Lavaggio tappeti & divani', 'Pulizia esterni & balconi', 'Gestione rifiuti', 'Riassortimento kit cortesia', 'Pulizie post-cantiere', 'Pulizie fine locazione', 'Controllo qualità & ispezioni'],
  manutentore: ['Idraulica', 'Elettricità', 'Climatizzazione', 'Caldaie & gas', 'Muratura', 'Falegnameria', 'Imbiancatura', 'Serramenti & infissi', 'Serrature', 'Antifurto & videosorveglianza', 'Domotica', 'Tuttofare', 'Elettrodomestici', 'Giardinaggio & esterni', 'Disinfestazione']
};
// Tipologie di immobili che un Cleaner Manager può gestire
const CLEANER_PROPERTY_TYPES: string[] = ['Appartamento', 'Monolocale', 'Loft', 'Attico', 'Villa', 'Villetta a schiera', 'B&B', 'Casa vacanze', 'Agriturismo', 'Dimora storica', 'Chalet / Baita', 'Boutique hotel', 'Camera in struttura', 'Glamping / Tiny house', 'Ufficio'];
// Raggruppamento dei servizi cleaner in categorie collassabili (i nomi devono
// corrispondere esattamente a ROLE_TRADES.cleaner)
const SERVICE_CATEGORIES: {
  label: string;
  items: string[];
}[] = [{
  label: 'Pulizie core',
  items: ['Pulizie turnover', 'Check-in / Check-out', 'Pulizie ordinarie', 'Deep cleaning']
}, {
  label: 'Igiene & sanificazione',
  items: ['Sanificazione', 'Igienizzazione bagni', 'Gestione rifiuti']
}, {
  label: 'Biancheria & comfort',
  items: ['Cambio biancheria', 'Lavanderia & stireria', 'Rifacimento letti', 'Riassortimento kit cortesia']
}, {
  label: 'Specializzate',
  items: ['Pulizia cucina & elettrodomestici', 'Pulizia vetri & infissi', 'Trattamento pavimenti', 'Lavaggio tappeti & divani', 'Pulizia esterni & balconi', 'Pulizie post-cantiere', 'Pulizie fine locazione', 'Controllo qualità & ispezioni']
}];
// Raggruppamento delle competenze del manutentore (i nomi devono corrispondere
// esattamente a ROLE_TRADES.manutentore)
const MAN_SERVICE_CATEGORIES: {
  label: string;
  items: string[];
}[] = [{
  label: 'Impianti',
  items: ['Idraulica', 'Elettricità', 'Climatizzazione', 'Caldaie & gas']
}, {
  label: 'Edilizia & finiture',
  items: ['Muratura', 'Falegnameria', 'Imbiancatura', 'Serramenti & infissi']
}, {
  label: 'Sicurezza & accessi',
  items: ['Serrature', 'Antifurto & videosorveglianza', 'Domotica']
}, {
  label: 'Generali',
  items: ['Tuttofare', 'Elettrodomestici', 'Giardinaggio & esterni', 'Disinfestazione']
}];
const PROPERTY_SECTION_KEY = '__immobili';
const CSV_TEMPLATE = 'nome,indirizzo,citta,mq,piano,ascensore\nVilla Sunset,Via dei Giardini 12,Milano,85,3,si\n';
// Cleaner Manager onboarding CSV templates
const CLEANER_CSV_TEMPLATE = 'nome,cognome,telefono,email,zona\nLucia,Verdi,+39 333 1234567,lucia@esempio.it,Milano · Navigli\n';
const CLIENTI_CSV_TEMPLATE = 'nome,indirizzo,citta,referente,telefono\nVilla Sunset,Via dei Giardini 12,Milano,Massimo Giri,+39 333 7654321\n';
const PIANIFICATI_CSV_TEMPLATE = 'data,ora,cliente,indirizzo,servizio,cleaner\n2026-06-10,10:00,Villa Sunset,Via dei Giardini 12,Pulizia turnover,Lucia Verdi\n';
// Manutentore onboarding CSV templates
const MAN_TEAM_CSV_TEMPLATE = 'nome,cognome,telefono,email,specializzazione\nMarco,Bianchi,+39 333 1234567,marco@esempio.it,Idraulica\n';
const MAN_PIANIFICATI_CSV_TEMPLATE = 'data,ora,cliente,indirizzo,intervento,tecnico\n2026-06-10,10:00,Condominio Verdi,Via Roma 5,Sostituzione caldaia,Marco Bianchi\n';
const inputClass = 'w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none bg-white min-h-[44px]';
export function OnboardingPage() {
  const navigate = useNavigate();
  const screenInit = useScreenInit() as {
    step?: Step;
    role?: Role;
  };
  const [role, setRole] = useState<Role | null>(screenInit?.role ?? null);
  const [step, setStep] = useState<Step>(screenInit?.step ?? 'role');
  // Account form
  const [nome, setNome] = useState('');
  const [cognome, setCognome] = useState('');
  const [email, setEmail] = useState('');
  const [telefono, setTelefono] = useState('');
  const [password, setPassword] = useState('');
  // Host — proprietà
  const [propType, setPropType] = useState<'singolo' | 'multi' | null>(null);
  const [csvFile, setCsvFile] = useState<string | null>(null);
  // Host — canali
  const [connected, setConnected] = useState<Record<string, boolean>>({});
  const [connecting, setConnecting] = useState<string | null>(null);
  // CM / Man — zona & competenze (zona = manutentore; skills = servizi/competenze)
  const [zona, setZona] = useState('');
  const [skills, setSkills] = useState<string[]>([]);
  // Cleaner Manager — accordion: prima categoria aperta, resto chiuso
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    'Pulizie core': true,
    Impianti: true
  });
  const toggleSection = (key: string) => setOpenSections(prev => ({
    ...prev,
    [key]: !prev[key]
  }));
  // Cleaner Manager — tipologie di immobili gestite (opzionale)
  const [propertyTypes, setPropertyTypes] = useState<string[]>([]);
  // Cleaner Manager — CSV imports
  const [cleanerCsv, setCleanerCsv] = useState<string | null>(null);
  const [clientiCsv, setClientiCsv] = useState<string | null>(null);
  const [pianificatiCsv, setPianificatiCsv] = useState<string | null>(null);
  const steps = role ? ROLE_STEPS[role] : [];
  const currentIndex = steps.indexOf(step);
  const totalSteps = steps.filter(s => s !== 'done').length;
  const finish = () => {
    localStorage.setItem('cleanai_onboarded', '1');
    navigate(role ? ROLE_ROUTES[role] : '/dashboard');
  };
  const goNext = () => {
    const next = steps[currentIndex + 1];
    if (next) setStep(next);
  };
  const goBack = () => {
    if (currentIndex <= 0) {
      setStep('role');
      setRole(null);
      return;
    }
    setStep(steps[currentIndex - 1]);
  };
  const selectRole = (r: Role) => {
    setRole(r);
    setStep('account');
  };
  const toggleChannel = (id: string) => {
    if (connected[id] || connecting) return;
    setConnecting(id);
    setTimeout(() => {
      setConnecting(null);
      setConnected(c => ({
        ...c,
        [id]: true
      }));
    }, 1200);
  };
  const togglePropertyType = (t: string) => {
    setPropertyTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  };
  const toggleSkill = (s: string) => {
    setSkills(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  };
  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template-proprieta-cleanai.csv';
    a.click();
    URL.revokeObjectURL(url);
  };
  const onCsvSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setCsvFile(f.name);
  };
  // Generic CSV template download (cleaner / clienti / pianificati)
  const downloadCsv = (content: string, fileName: string) => {
    const blob = new Blob([content], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };
  const onFilePicked = (e: React.ChangeEvent<HTMLInputElement>, setter: (v: string | null) => void) => {
    const f = e.target.files?.[0];
    if (f) setter(f.name);
  };
  // ---- Validation ----
  const isAccountValid = nome.trim() !== '' && cognome.trim() !== '' && email.trim() !== '';
  const isServiziValid = skills.length > 0;
  const isProprietaValid = propType === 'singolo' || propType === 'multi' && csvFile !== null || propType === 'multi';
  const channels = [{
    id: 'airbnb',
    name: 'Airbnb',
    color: '#FF5A5F'
  }, {
    id: 'booking',
    name: 'Booking.com',
    color: '#003580'
  }, {
    id: 'channel',
    name: 'Channel manager (Vikey, Smoobu…)',
    color: '#1db6e4'
  }];
  return <div className="min-h-screen bg-background flex flex-col" data-id="element-3398">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-10" data-id="element-3399">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 py-4 flex items-center justify-between gap-4" data-id="element-3400">
          <span className="text-sm font-bold text-navy" data-id="element-3401">
            Clean<span className="text-coral" data-id="element-3402">AI</span>
          </span>
          <div className="flex items-center gap-4" data-id="element-3403">
            {step !== 'role' && step !== 'done' && <span className="text-xs text-muted-foreground" data-id="element-3404">
                Passo {currentIndex + 1} di {totalSteps}
              </span>}
            <button onClick={finish} className="text-xs font-medium text-muted-foreground hover:text-foreground border border-border rounded-full px-3 py-1.5 transition-colors hover:bg-muted" data-id="element-3405">
              Salta (demo) →
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-4 lg:px-8 py-8 lg:py-12" data-id="element-3406">
        {/* Progress bar (hidden on role + done) */}
        {step !== 'role' && step !== 'done' && <div className="flex items-center gap-2 mb-8" data-id="element-3407">
            {steps.filter(s => s !== 'done').map((s, i) => <div key={s} className={`h-1 flex-1 rounded-full transition-colors ${i <= currentIndex ? 'bg-coral' : 'bg-muted'}`} data-id="element-3408" />)}
          </div>}

        {/* ───────── STEP: Role selection ───────── */}
        {step === 'role' && <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-3409">
            <h1 className="text-[28px] lg:text-3xl font-[600] text-navy mb-2" data-id="element-3410">
              Crea il tuo account
            </h1>
            <p className="text-sm lg:text-base text-muted-foreground mb-8" data-id="element-3411">
              Per iniziare, dicci chi sei. Personalizzeremo l'esperienza in base
              al tuo ruolo.
            </p>

            <div className="space-y-3" data-id="element-3412">
              {ROLE_OPTIONS.map(opt => <button key={opt.role} onClick={() => selectRole(opt.role)} className="w-full flex items-center gap-4 text-left bg-white border border-border rounded-xl p-4 lg:p-5 hover:border-coral hover:bg-coral/5 transition-colors group" data-id="element-3413">
                  <span className="w-12 h-12 rounded-xl bg-muted group-hover:bg-coral/10 flex items-center justify-center flex-shrink-0 text-navy group-hover:text-coral transition-colors" data-id="element-3414">
                    {opt.icon}
                  </span>
                  <span className="min-w-0 flex-1" data-id="element-3415">
                    <span className="block text-sm font-semibold text-navy" data-id="element-3416">
                      {opt.label}
                    </span>
                    <span className="block text-xs text-muted-foreground mt-0.5" data-id="element-3417">
                      {opt.desc}
                    </span>
                  </span>
                  <span className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-border group-hover:border-coral transition-colors" data-id="element-3418" />
                </button>)}
            </div>

            <p className="text-xs text-muted-foreground text-center mt-8" data-id="element-3419">
              Hai già un account?{' '}
              <button onClick={() => navigate('/login')} className="font-medium text-coral hover:underline" data-id="element-3420">
                Accedi
              </button>
            </p>
          </div>}

        {/* ───────── STEP: Account ───────── */}
        {step === 'account' && <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-3421">
            <h1 className="text-2xl lg:text-3xl font-bold text-navy mb-2" data-id="element-3422">
              I tuoi dati
            </h1>
            <p className="text-sm lg:text-base text-muted-foreground mb-8" data-id="element-3423">
              Usiamo queste informazioni per creare il tuo profilo e
              contattarti.
            </p>

            <div className="bg-white border border-border rounded-xl p-5 lg:p-6 mb-6 space-y-5" data-id="element-3424">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-3425">
                <div data-id="element-3426">
                  <label className="block text-sm font-medium text-foreground mb-1.5" data-id="element-3427">
                    Nome *
                  </label>
                  <input type="text" value={nome} onChange={e => setNome(e.target.value)} placeholder="Es. Massimo" className={inputClass} data-id="element-3428" />
                </div>
                <div data-id="element-3429">
                  <label className="block text-sm font-medium text-foreground mb-1.5" data-id="element-3430">
                    Cognome *
                  </label>
                  <input type="text" value={cognome} onChange={e => setCognome(e.target.value)} placeholder="Es. Giri" className={inputClass} data-id="element-3431" />
                </div>
              </div>

              <div data-id="element-3432">
                <label className="block text-sm font-medium text-foreground mb-1.5" data-id="element-3433">
                  Email *
                </label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="nome@esempio.it" className={inputClass} data-id="element-3434" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-3435">
                <div data-id="element-3436">
                  <label className="block text-sm font-medium text-foreground mb-1.5" data-id="element-3437">
                    Telefono
                  </label>
                  <input type="tel" value={telefono} onChange={e => setTelefono(e.target.value)} placeholder="Es. +39 333 1234567" className={inputClass} data-id="element-3438" />
                </div>
                <div data-id="element-3439">
                  <label className="block text-sm font-medium text-foreground mb-1.5" data-id="element-3440">
                    Password
                  </label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" className={inputClass} data-id="element-3441" />
                </div>
              </div>
            </div>

            <StepNav onBack={goBack} onNext={goNext} nextDisabled={!isAccountValid} data-id="element-3442" />
          </div>}

        {/* ───────── STEP: Host proprietà ───────── */}
        {step === 'proprieta' && <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-3443">
            <h1 className="text-2xl lg:text-3xl font-bold text-navy mb-2" data-id="element-3444">
              Le tue proprietà
            </h1>
            <p className="text-sm lg:text-base text-muted-foreground mb-8" data-id="element-3445">
              Quante strutture gestisci? Potrai aggiungere foto e dettagli più
              avanti.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-id="element-3446">
              <button onClick={() => setPropType('singolo')} className={`text-left rounded-xl border p-5 transition-colors ${propType === 'singolo' ? 'border-coral bg-coral/5' : 'border-border bg-white hover:border-gray-300'}`} data-id="element-3447">
                <span className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 ${propType === 'singolo' ? 'bg-coral/10 text-coral' : 'bg-muted text-navy'}`} data-id="element-3448">
                  <Home size={20} data-id="element-3449" />
                </span>
                <span className="block text-sm font-semibold text-navy" data-id="element-3450">
                  Singolo appartamento / B&B
                </span>
                <span className="block text-xs text-muted-foreground mt-1" data-id="element-3451">
                  Gestisci una sola struttura.
                </span>
              </button>

              <button onClick={() => setPropType('multi')} className={`text-left rounded-xl border p-5 transition-colors ${propType === 'multi' ? 'border-coral bg-coral/5' : 'border-border bg-white hover:border-gray-300'}`} data-id="element-3452">
                <span className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 ${propType === 'multi' ? 'bg-coral/10 text-coral' : 'bg-muted text-navy'}`} data-id="element-3453">
                  <Building2 size={20} data-id="element-3454" />
                </span>
                <span className="block text-sm font-semibold text-navy" data-id="element-3455">
                  Più appartamenti
                </span>
                <span className="block text-xs text-muted-foreground mt-1" data-id="element-3456">
                  Carica tutte le strutture insieme via CSV.
                </span>
              </button>
            </div>

            {/* CSV uploader — only for multi */}
            {propType === 'multi' && <div className="bg-white border border-border rounded-xl p-5 lg:p-6 mb-6 animate-in fade-in slide-in-from-top-2 duration-300" data-id="element-3457">
                <div className="flex items-center justify-between gap-3 mb-4 flex-wrap" data-id="element-3458">
                  <div className="flex items-center gap-2" data-id="element-3459">
                    <FileSpreadsheet size={18} className="text-coral" data-id="element-3460" />
                    <h3 className="text-sm font-semibold text-navy" data-id="element-3461">
                      Carica le tue proprietà
                    </h3>
                  </div>
                  <button onClick={downloadTemplate} className="inline-flex items-center gap-1.5 text-xs font-medium text-coral hover:underline" data-id="element-3462">
                    <Download size={14} data-id="element-3463" />
                    Scarica template CSV
                  </button>
                </div>

                <label className="block border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-coral hover:bg-coral/5 transition-colors" data-id="element-3464">
                  <input type="file" accept=".csv" className="hidden" onChange={onCsvSelected} data-id="element-3465" />
                  {csvFile ? <span className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700" data-id="element-3466">
                      <CheckCircle size={16} className="text-emerald-600" data-id="element-3467" />
                      {csvFile}
                    </span> : <span className="flex flex-col items-center gap-2" data-id="element-3468">
                      <UploadCloud size={24} className="text-muted-foreground" data-id="element-3469" />
                      <span className="text-sm font-medium text-navy" data-id="element-3470">
                        Trascina o seleziona il file CSV
                      </span>
                      <span className="text-xs text-muted-foreground" data-id="element-3471">
                        Usa il template per evitare errori di importazione.
                      </span>
                    </span>}
                </label>
              </div>}

            <StepNav onBack={goBack} onNext={goNext} nextDisabled={!isProprietaValid} data-id="element-3472" />
          </div>}

        {/* ───────── STEP: Host canali ───────── */}
        {step === 'canali' && <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-3473">
            <h1 className="text-2xl lg:text-3xl font-bold text-navy mb-2" data-id="element-3474">
              Collega i tuoi canali
            </h1>
            <p className="text-sm lg:text-base text-muted-foreground mb-6" data-id="element-3475">
              Sincronizza i check-in/out: pianificheremo le pulizie in
              automatico, senza inserimenti manuali.
            </p>

            <div className="bg-coral/5 border border-coral/20 rounded-xl p-4 flex items-start gap-3 mb-6" data-id="element-3476">
              <Info className="text-coral flex-shrink-0 mt-0.5" size={18} data-id="element-3477" />
              <p className="text-xs text-foreground leading-relaxed" data-id="element-3478">
                Puoi collegare i canali ora o saltare e farlo più tardi dalle
                Impostazioni.
              </p>
            </div>

            <div className="space-y-3 mb-6" data-id="element-3479">
              {channels.map(ch => {
            const isConn = connected[ch.id];
            const isBusy = connecting === ch.id;
            return <div key={ch.id} className="flex items-center gap-3 bg-white border border-border rounded-xl p-4" data-id="element-3480">
                    <span className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{
                backgroundColor: `${ch.color}1a`,
                color: ch.color
              }} data-id="element-3481">
                      <Link2 size={18} data-id="element-3482" />
                    </span>
                    <span className="flex-1 min-w-0 text-sm font-medium text-navy truncate" data-id="element-3483">
                      {ch.name}
                    </span>
                    {isConn ? <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 flex-shrink-0" data-id="element-3484">
                        <CheckCircle size={16} className="text-emerald-600" data-id="element-3485" />
                        Collegato
                      </span> : <button onClick={() => toggleChannel(ch.id)} disabled={isBusy} className="flex-shrink-0 bg-white border border-border text-navy rounded-lg px-4 h-9 text-sm font-semibold hover:bg-muted transition-colors disabled:opacity-60 inline-flex items-center gap-2" data-id="element-3486">
                        {isBusy ? <>
                            <span className="w-3.5 h-3.5 border-2 border-coral/30 border-t-coral rounded-full animate-spin" data-id="element-3487" />
                            Collego…
                          </> : 'Collega'}
                      </button>}
                  </div>;
          })}
            </div>

            <StepNav onBack={goBack} onNext={goNext} nextLabel={Object.keys(connected).length > 0 ? 'Continua' : 'Salta e continua'} data-id="element-3488" />
          </div>}

        {/* ───────── STEP: Manutentore — competenze ───────── */}
        {step === 'servizi' && role === 'manutentore' && <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-3489">
            <h1 className="text-2xl lg:text-3xl font-bold text-navy mb-2" data-id="element-3490">
              Le tue competenze
            </h1>
            <p className="text-sm lg:text-base text-muted-foreground mb-8" data-id="element-3491">
              Seleziona i lavori che esegui: ti proporremo solo gli interventi
              in linea con le tue specializzazioni.
            </p>

            <div className="bg-white border border-border rounded-xl p-5 lg:p-6 mb-6" data-id="element-3492">
              {/* Summary */}
              <div className="flex items-center justify-between gap-3 pb-4 mb-2 border-b border-border" data-id="element-3493">
                <span className="text-sm font-medium text-foreground" data-id="element-3494">
                  Competenze *
                </span>
                <span className="text-xs font-medium text-muted-foreground" data-id="element-3495">
                  Selezionate:{' '}
                  <span className="text-coral font-semibold" data-id="element-3496">
                    {skills.length}
                  </span>
                </span>
              </div>

              {/* Competence categories accordion */}
              <div className="divide-y divide-border" data-id="element-3497">
                {MAN_SERVICE_CATEGORIES.map(cat => {
              const isOpen = !!openSections[cat.label];
              const selectedInCat = cat.items.filter(s => skills.includes(s)).length;
              return <div key={cat.label} data-id="element-3498">
                      <button type="button" onClick={() => toggleSection(cat.label)} aria-expanded={isOpen} className="w-full flex items-center justify-between gap-3 py-3 min-h-[44px] text-left" data-id="element-3499">
                        <span className="flex items-center gap-2" data-id="element-3500">
                          <span className="text-sm font-semibold text-navy" data-id="element-3501">
                            {cat.label}
                          </span>
                          {selectedInCat > 0 && <span className="text-[11px] font-semibold text-coral bg-coral/10 rounded-full px-2 py-0.5" data-id="element-3502">
                              {selectedInCat}
                            </span>}
                        </span>
                        <ChevronDown size={18} className={`text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-3503" />
                      </button>
                      {isOpen && <div className="flex flex-wrap gap-2 pb-4 animate-in fade-in slide-in-from-top-1 duration-200" data-id="element-3504">
                          {cat.items.map(s => {
                    const active = skills.includes(s);
                    return <button key={s} onClick={() => toggleSkill(s)} aria-pressed={active} className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${active ? 'bg-coral text-white border-coral' : 'bg-white text-gray-600 border-border hover:border-gray-300'}`} data-id="element-3505">
                                {s}
                              </button>;
                  })}
                        </div>}
                    </div>;
            })}

                {/* Property types — optional accordion section */}
                <div data-id="element-3506">
                  <button type="button" onClick={() => toggleSection(PROPERTY_SECTION_KEY)} aria-expanded={!!openSections[PROPERTY_SECTION_KEY]} className="w-full flex items-center justify-between gap-3 py-3 min-h-[44px] text-left" data-id="element-3507">
                    <span className="flex items-center gap-2 flex-wrap" data-id="element-3508">
                      <span className="text-sm font-semibold text-navy" data-id="element-3509">
                        Tipologie di immobili su cui intervieni
                      </span>
                      <span className="text-[10px] font-medium text-muted-foreground bg-muted rounded-full px-2 py-0.5" data-id="element-3510">
                        Opzionale
                      </span>
                      {propertyTypes.length > 0 && <span className="text-[11px] font-semibold text-coral bg-coral/10 rounded-full px-2 py-0.5" data-id="element-3511">
                          {propertyTypes.length}
                        </span>}
                    </span>
                    <ChevronDown size={18} className={`text-muted-foreground transition-transform ${openSections[PROPERTY_SECTION_KEY] ? 'rotate-180' : ''}`} data-id="element-3512" />
                  </button>
                  {openSections[PROPERTY_SECTION_KEY] && <div className="pb-4 animate-in fade-in slide-in-from-top-1 duration-200" data-id="element-3513">
                      <p className="text-xs text-muted-foreground mb-3" data-id="element-3514">
                        Aiuta a proporti gli interventi sugli immobili che
                        conosci meglio.
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-3515">
                        {CLEANER_PROPERTY_TYPES.map(t => {
                    const active = propertyTypes.includes(t);
                    return <button key={t} onClick={() => togglePropertyType(t)} aria-pressed={active} className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${active ? 'bg-coral text-white border-coral' : 'bg-white text-gray-600 border-border hover:border-gray-300'}`} data-id="element-3516">
                              {t}
                            </button>;
                  })}
                      </div>
                    </div>}
                </div>
              </div>
            </div>

            <StepNav onBack={goBack} onNext={goNext} nextDisabled={!isServiziValid} data-id="element-3517" />
          </div>}

        {/* ───────── STEP: Manutentore — carica collaboratori (CSV) ───────── */}
        {step === 'team' && role === 'manutentore' && <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-3518">
            <span className="inline-flex items-center text-xs font-semibold text-coral bg-coral/10 rounded-full px-2.5 py-1 mb-3" data-id="element-3519">
              La tua squadra
            </span>
            <h1 className="text-2xl lg:text-3xl font-bold text-navy mb-2" data-id="element-3520">
              Carica i tuoi collaboratori
            </h1>
            <p className="text-sm lg:text-base text-muted-foreground mb-8" data-id="element-3521">
              Importa i tecnici che lavorano con te: potrai assegnare loro gli
              interventi in pochi tap.
            </p>

            <CsvImportCard icon={<Users size={20} className="text-coral" data-id="element-3523" />} title="Importa la squadra da CSV" description="Un tecnico per riga, con i contatti e la specializzazione." expectedColumns={['nome', 'cognome', 'telefono', 'email', 'specializzazione']} fileName={cleanerCsv} onFile={e => onFilePicked(e, setCleanerCsv)} onClear={() => setCleanerCsv(null)} onDownloadTemplate={() => downloadCsv(MAN_TEAM_CSV_TEMPLATE, 'template-tecnici-cleanai.csv')} data-id="element-3522" />

            <div className="flex items-start gap-2 text-xs text-muted-foreground mb-2" data-id="element-3524">
              <Info size={14} className="text-muted-foreground flex-shrink-0 mt-0.5" data-id="element-3525" />
              <p data-id="element-3526">
                Lavori da solo? Puoi saltare questo passaggio e gestire tutto in
                autonomia.
              </p>
            </div>

            <StepNav onBack={goBack} onNext={goNext} nextLabel={cleanerCsv ? 'Continua' : 'Salta e continua'} data-id="element-3527" />
          </div>}

        {/* ───────── STEP: Manutentore — carica clienti (CSV) ───────── */}
        {step === 'clienti' && role === 'manutentore' && <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-3528">
            <span className="inline-flex items-center text-xs font-semibold text-coral bg-coral/10 rounded-full px-2.5 py-1 mb-3" data-id="element-3529">
              I tuoi clienti
            </span>
            <h1 className="text-2xl lg:text-3xl font-bold text-navy mb-2" data-id="element-3530">
              Carica i tuoi clienti
            </h1>
            <p className="text-sm lg:text-base text-muted-foreground mb-8" data-id="element-3531">
              Porta in piattaforma host, strutture e condomini che già segui:
              saranno subito collegati ai tuoi interventi.
            </p>

            <CsvImportCard icon={<Building2 size={20} className="text-coral" data-id="element-3533" />} title="Importa i clienti da CSV" description="Un cliente per riga, con indirizzo e referente." expectedColumns={['nome', 'indirizzo', 'citta', 'referente', 'telefono']} fileName={clientiCsv} onFile={e => onFilePicked(e, setClientiCsv)} onClear={() => setClientiCsv(null)} onDownloadTemplate={() => downloadCsv(CLIENTI_CSV_TEMPLATE, 'template-clienti-cleanai.csv')} data-id="element-3532" />

            <StepNav onBack={goBack} onNext={goNext} nextLabel={clientiCsv ? 'Continua' : 'Salta e continua'} data-id="element-3534" />
          </div>}

        {/* ───────── STEP: Manutentore — interventi pianificati (CSV) ───────── */}
        {step === 'pianificati' && role === 'manutentore' && <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-3535">
            <span className="inline-flex items-center text-xs font-semibold text-coral bg-coral/10 rounded-full px-2.5 py-1 mb-3" data-id="element-3536">
              La tua agenda
            </span>
            <h1 className="text-2xl lg:text-3xl font-bold text-navy mb-2" data-id="element-3537">
              Interventi pianificati
            </h1>
            <p className="text-sm lg:text-base text-muted-foreground mb-8" data-id="element-3538">
              Precarica gli interventi già fissati: li ritroverai pronti nei
              tuoi incarichi, senza reinserirli a mano.
            </p>

            <CsvImportCard icon={<CalendarClock size={20} className="text-coral" data-id="element-3540" />} title="Importa l'agenda da CSV" description="Un intervento per riga, con data, cliente e tecnico assegnato." expectedColumns={['data', 'ora', 'cliente', 'indirizzo', 'intervento', 'tecnico']} fileName={pianificatiCsv} onFile={e => onFilePicked(e, setPianificatiCsv)} onClear={() => setPianificatiCsv(null)} onDownloadTemplate={() => downloadCsv(MAN_PIANIFICATI_CSV_TEMPLATE, 'template-interventi-man-cleanai.csv')} data-id="element-3539">
              {/* Anteprima illustrativa di come appariranno gli interventi importati */}
              <div className="mt-5" data-id="element-3541">
                <p className="text-[11px] font-medium text-muted-foreground mb-2" data-id="element-3542">
                  Anteprima — esempio di come appariranno
                </p>
                <div className="rounded-lg border border-border divide-y divide-border" data-id="element-3543">
                  {[{
                data: 'Mar 10 Giu · 10:00',
                cliente: 'Condominio Verdi',
                servizio: 'Sostituzione caldaia'
              }, {
                data: 'Mer 11 Giu · 14:30',
                cliente: 'Villa Sunset',
                servizio: 'Riparazione impianto elettrico'
              }, {
                data: 'Gio 12 Giu · 09:00',
                cliente: 'B&B Duomo',
                servizio: 'Manutenzione climatizzatore'
              }].map((row, i) => <div key={i} className="flex items-center gap-3 px-3 py-2.5 text-xs" data-id="element-3544">
                      <Calendar size={14} className="text-coral flex-shrink-0" data-id="element-3545" />
                      <span className="font-medium text-navy w-36 flex-shrink-0" data-id="element-3546">
                        {row.data}
                      </span>
                      <span className="text-foreground truncate flex-1" data-id="element-3547">
                        {row.cliente}
                      </span>
                      <span className="text-muted-foreground hidden sm:block truncate" data-id="element-3548">
                        {row.servizio}
                      </span>
                    </div>)}
                </div>
              </div>
            </CsvImportCard>

            <StepNav onBack={goBack} onNext={goNext} nextLabel={pianificatiCsv ? 'Continua' : 'Salta e continua'} data-id="element-3549" />
          </div>}

        {/* ───────── STEP: Cleaner — servizi ───────── */}
        {step === 'servizi' && role === 'cleaner' && <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-3550">
            <h1 className="text-2xl lg:text-3xl font-bold text-navy mb-2" data-id="element-3551">
              I servizi che offri
            </h1>
            <p className="text-sm lg:text-base text-muted-foreground mb-8" data-id="element-3552">
              Seleziona i servizi gestiti dal tuo team: li useremo per proporti
              gli incarichi giusti.
            </p>

            <div className="bg-white border border-border rounded-xl p-5 lg:p-6 mb-6" data-id="element-3553">
              {/* Summary */}
              <div className="flex items-center justify-between gap-3 pb-4 mb-2 border-b border-border" data-id="element-3554">
                <span className="text-sm font-medium text-foreground" data-id="element-3555">
                  Servizi che offri *
                </span>
                <span className="text-xs font-medium text-muted-foreground" data-id="element-3556">
                  Selezionati:{' '}
                  <span className="text-coral font-semibold" data-id="element-3557">
                    {skills.length}
                  </span>
                </span>
              </div>

              {/* Service categories accordion */}
              <div className="divide-y divide-border" data-id="element-3558">
                {SERVICE_CATEGORIES.map(cat => {
              const isOpen = !!openSections[cat.label];
              const selectedInCat = cat.items.filter(s => skills.includes(s)).length;
              return <div key={cat.label} data-id="element-3559">
                      <button type="button" onClick={() => toggleSection(cat.label)} aria-expanded={isOpen} className="w-full flex items-center justify-between gap-3 py-3 min-h-[44px] text-left" data-id="element-3560">
                        <span className="flex items-center gap-2" data-id="element-3561">
                          <span className="text-sm font-semibold text-navy" data-id="element-3562">
                            {cat.label}
                          </span>
                          {selectedInCat > 0 && <span className="text-[11px] font-semibold text-coral bg-coral/10 rounded-full px-2 py-0.5" data-id="element-3563">
                              {selectedInCat}
                            </span>}
                        </span>
                        <ChevronDown size={18} className={`text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-3564" />
                      </button>
                      {isOpen && <div className="flex flex-wrap gap-2 pb-4 animate-in fade-in slide-in-from-top-1 duration-200" data-id="element-3565">
                          {cat.items.map(s => {
                    const active = skills.includes(s);
                    return <button key={s} onClick={() => toggleSkill(s)} aria-pressed={active} className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${active ? 'bg-coral text-white border-coral' : 'bg-white text-gray-600 border-border hover:border-gray-300'}`} data-id="element-3566">
                                {s}
                              </button>;
                  })}
                        </div>}
                    </div>;
            })}

                {/* Property types — optional accordion section */}
                <div data-id="element-3567">
                  <button type="button" onClick={() => toggleSection(PROPERTY_SECTION_KEY)} aria-expanded={!!openSections[PROPERTY_SECTION_KEY]} className="w-full flex items-center justify-between gap-3 py-3 min-h-[44px] text-left" data-id="element-3568">
                    <span className="flex items-center gap-2 flex-wrap" data-id="element-3569">
                      <span className="text-sm font-semibold text-navy" data-id="element-3570">
                        Tipologie di immobili gestite
                      </span>
                      <span className="text-[10px] font-medium text-muted-foreground bg-muted rounded-full px-2 py-0.5" data-id="element-3571">
                        Opzionale
                      </span>
                      {propertyTypes.length > 0 && <span className="text-[11px] font-semibold text-coral bg-coral/10 rounded-full px-2 py-0.5" data-id="element-3572">
                          {propertyTypes.length}
                        </span>}
                    </span>
                    <ChevronDown size={18} className={`text-muted-foreground transition-transform ${openSections[PROPERTY_SECTION_KEY] ? 'rotate-180' : ''}`} data-id="element-3573" />
                  </button>
                  {openSections[PROPERTY_SECTION_KEY] && <div className="pb-4 animate-in fade-in slide-in-from-top-1 duration-200" data-id="element-3574">
                      <p className="text-xs text-muted-foreground mb-3" data-id="element-3575">
                        Aiuta a proporti gli immobili più adatti al tuo team.
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-3576">
                        {CLEANER_PROPERTY_TYPES.map(t => {
                    const active = propertyTypes.includes(t);
                    return <button key={t} onClick={() => togglePropertyType(t)} aria-pressed={active} className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${active ? 'bg-coral text-white border-coral' : 'bg-white text-gray-600 border-border hover:border-gray-300'}`} data-id="element-3577">
                              {t}
                            </button>;
                  })}
                      </div>
                    </div>}
                </div>
              </div>
            </div>

            <StepNav onBack={goBack} onNext={goNext} nextDisabled={!isServiziValid} data-id="element-3578" />
          </div>}

        {/* ───────── STEP: Cleaner — carica cleaner (CSV) ───────── */}
        {step === 'cleaner' && role === 'cleaner' && <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-3579">
            <span className="inline-flex items-center text-xs font-semibold text-coral bg-coral/10 rounded-full px-2.5 py-1 mb-3" data-id="element-3580">
              Il tuo team
            </span>
            <h1 className="text-2xl lg:text-3xl font-bold text-navy mb-2" data-id="element-3581">
              Carica i tuoi cleaner
            </h1>
            <p className="text-sm lg:text-base text-muted-foreground mb-8" data-id="element-3582">
              Importa gli addetti che lavorano con te: potrai assegnare loro gli
              incarichi in pochi tap.
            </p>

            <CsvImportCard icon={<Users size={20} className="text-coral" data-id="element-3584" />} title="Importa il team da CSV" description="Un addetto per riga, con i dati di contatto e la zona operativa." expectedColumns={['nome', 'cognome', 'telefono', 'email', 'zona']} fileName={cleanerCsv} onFile={e => onFilePicked(e, setCleanerCsv)} onClear={() => setCleanerCsv(null)} onDownloadTemplate={() => downloadCsv(CLEANER_CSV_TEMPLATE, 'template-cleaner-cleanai.csv')} data-id="element-3583" />

            <div className="flex items-start gap-2 text-xs text-muted-foreground mb-2" data-id="element-3585">
              <Info size={14} className="text-muted-foreground flex-shrink-0 mt-0.5" data-id="element-3586" />
              <p data-id="element-3587">
                Preferisci aggiungerli a mano? Potrai farlo in qualsiasi momento
                dalla sezione{' '}
                <span className="font-medium text-navy" data-id="element-3588">Team</span>.
              </p>
            </div>

            <StepNav onBack={goBack} onNext={goNext} nextLabel={cleanerCsv ? 'Continua' : 'Salta e continua'} data-id="element-3589" />
          </div>}

        {/* ───────── STEP: Cleaner — carica clienti (CSV) ───────── */}
        {step === 'clienti' && role === 'cleaner' && <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-3590">
            <span className="inline-flex items-center text-xs font-semibold text-coral bg-coral/10 rounded-full px-2.5 py-1 mb-3" data-id="element-3591">
              Le tue strutture
            </span>
            <h1 className="text-2xl lg:text-3xl font-bold text-navy mb-2" data-id="element-3592">
              Carica i tuoi clienti
            </h1>
            <p className="text-sm lg:text-base text-muted-foreground mb-8" data-id="element-3593">
              Porta in piattaforma gli host e gli immobili che già gestisci:
              saranno subito collegati ai tuoi incarichi.
            </p>

            <CsvImportCard icon={<Building2 size={20} className="text-coral" data-id="element-3595" />} title="Importa i clienti da CSV" description="Una struttura per riga, con indirizzo e referente." expectedColumns={['nome', 'indirizzo', 'citta', 'referente', 'telefono']} fileName={clientiCsv} onFile={e => onFilePicked(e, setClientiCsv)} onClear={() => setClientiCsv(null)} onDownloadTemplate={() => downloadCsv(CLIENTI_CSV_TEMPLATE, 'template-clienti-cleanai.csv')} data-id="element-3594" />

            <StepNav onBack={goBack} onNext={goNext} nextLabel={clientiCsv ? 'Continua' : 'Salta e continua'} data-id="element-3596" />
          </div>}

        {/* ───────── STEP: Cleaner — interventi pianificati (CSV) ───────── */}
        {step === 'pianificati' && role === 'cleaner' && <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-3597">
            <span className="inline-flex items-center text-xs font-semibold text-coral bg-coral/10 rounded-full px-2.5 py-1 mb-3" data-id="element-3598">
              La tua agenda
            </span>
            <h1 className="text-2xl lg:text-3xl font-bold text-navy mb-2" data-id="element-3599">
              Interventi pianificati
            </h1>
            <p className="text-sm lg:text-base text-muted-foreground mb-8" data-id="element-3600">
              Precarica le pulizie già in calendario: le ritroverai pronte nei
              tuoi incarichi, senza reinserirle a mano.
            </p>

            <CsvImportCard icon={<CalendarClock size={20} className="text-coral" data-id="element-3602" />} title="Importa l'agenda da CSV" description="Un intervento per riga, con data, struttura e cleaner assegnato." expectedColumns={['data', 'ora', 'cliente', 'indirizzo', 'servizio', 'cleaner']} fileName={pianificatiCsv} onFile={e => onFilePicked(e, setPianificatiCsv)} onClear={() => setPianificatiCsv(null)} onDownloadTemplate={() => downloadCsv(PIANIFICATI_CSV_TEMPLATE, 'template-interventi-cleanai.csv')} data-id="element-3601">
              {/* Anteprima illustrativa di come appariranno gli interventi importati */}
              <div className="mt-5" data-id="element-3603">
                <p className="text-[11px] font-medium text-muted-foreground mb-2" data-id="element-3604">
                  Anteprima — esempio di come appariranno
                </p>
                <div className="rounded-lg border border-border divide-y divide-border" data-id="element-3605">
                  {[{
                data: 'Mar 10 Giu · 10:00',
                cliente: 'Villa Sunset',
                servizio: 'Pulizia turnover'
              }, {
                data: 'Mer 11 Giu · 14:30',
                cliente: 'Loft Navigli',
                servizio: 'Deep cleaning'
              }, {
                data: 'Gio 12 Giu · 09:00',
                cliente: 'B&B Duomo',
                servizio: 'Cambio biancheria'
              }].map((row, i) => <div key={i} className="flex items-center gap-3 px-3 py-2.5 text-xs" data-id="element-3606">
                      <Calendar size={14} className="text-coral flex-shrink-0" data-id="element-3607" />
                      <span className="font-medium text-navy w-36 flex-shrink-0" data-id="element-3608">
                        {row.data}
                      </span>
                      <span className="text-foreground truncate flex-1" data-id="element-3609">
                        {row.cliente}
                      </span>
                      <span className="text-muted-foreground hidden sm:block truncate" data-id="element-3610">
                        {row.servizio}
                      </span>
                    </div>)}
                </div>
              </div>
            </CsvImportCard>

            <StepNav onBack={goBack} onNext={goNext} nextLabel={pianificatiCsv ? 'Continua' : 'Salta e continua'} data-id="element-3611" />
          </div>}

        {/* ───────── STEP: Done — conferma + vantaggi + invito ───────── */}
        {step === 'done' && <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-3612">
            {/* Confirmation header */}
            <div className="text-center mb-8 lg:mb-10" data-id="element-3613">
              <div className="w-16 h-16 lg:w-20 lg:h-20 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-5 border border-emerald-200 animate-in zoom-in-95 duration-500" data-id="element-3614">
                <CheckCircle size={36} className="text-emerald-600" data-id="element-3615" />
              </div>
              <span className="inline-flex items-center text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 mb-4" data-id="element-3616">
                Account creato
              </span>
              <h1 className="text-2xl lg:text-3xl font-bold text-navy mb-2" data-id="element-3617">
                Benvenuto su CleanAI, {nome || 'benvenuto'}!
              </h1>
              <p className="text-sm lg:text-base text-muted-foreground max-w-md mx-auto leading-relaxed" data-id="element-3618">
                Il tuo profilo è pronto. Ecco cosa ti aspetta appena entri.
              </p>
            </div>

            {/* Benefits — spostati qui dallo step separato */}
            {role && <div className="bg-white border border-border rounded-2xl p-5 lg:p-8 mb-8" data-id="element-3619">
                <h2 className="text-sm font-semibold text-navy mb-5" data-id="element-3620">
                  Cosa puoi fare ora
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-5" data-id="element-3621">
                  {ROLE_BENEFITS[role].map((b, i) => <div key={i} className="flex items-start gap-3" data-id="element-3622">
                      <span className="w-10 h-10 rounded-xl bg-coral/10 flex items-center justify-center flex-shrink-0" data-id="element-3623">
                        {b.icon}
                      </span>
                      <div className="min-w-0" data-id="element-3624">
                        <h3 className="text-sm font-semibold text-navy mb-0.5" data-id="element-3625">
                          {b.title}
                        </h3>
                        <p className="text-xs text-muted-foreground leading-relaxed" data-id="element-3626">
                          {b.desc}
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>}

            {/* Invito ad entrare in piattaforma */}
            <button onClick={finish} className="w-full bg-coral text-white rounded-xl px-8 py-3.5 text-base font-semibold hover:bg-coral-600 transition-colors shadow-sm min-h-[52px] inline-flex items-center justify-center gap-2" data-id="element-3627">
              Entra in piattaforma
              <ArrowRight size={18} data-id="element-3628" />
            </button>
            <p className="text-xs text-muted-foreground text-center mt-3" data-id="element-3629">
              {role === 'host' ? 'Ti portiamo alla Dashboard per organizzare le tue pulizie.' : 'Ti portiamo alla Dashboard per vedere le opportunità in zona.'}
            </p>
          </div>}
      </main>
    </div>;
}
// Reusable CSV import card (Cleaner Manager onboarding: cleaner / clienti / pianificati)
function CsvImportCard({
  icon,
  title,
  description,
  expectedColumns,
  fileName,
  onFile,
  onClear,
  onDownloadTemplate,
  children
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  expectedColumns?: string[];
  fileName: string | null;
  onFile: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear?: () => void;
  onDownloadTemplate: () => void;
  children?: React.ReactNode;
}) {
  return <div className="bg-white border border-border rounded-xl overflow-hidden mb-6" data-id="element-3630">
      {/* Accent strip + header */}
      <div className="border-b border-border p-5 lg:p-6" data-id="element-3631">
        <div className="flex items-start justify-between gap-3 flex-wrap" data-id="element-3632">
          <div className="flex items-start gap-3 min-w-0" data-id="element-3633">
            <span className="w-10 h-10 rounded-xl bg-coral/10 flex items-center justify-center flex-shrink-0" data-id="element-3634">
              {icon}
            </span>
            <div className="min-w-0" data-id="element-3635">
              <h3 className="text-sm font-semibold text-navy" data-id="element-3636">{title}</h3>
              {description && <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed" data-id="element-3637">
                  {description}
                </p>}
            </div>
          </div>
          <button onClick={onDownloadTemplate} className="inline-flex items-center gap-1.5 text-xs font-medium text-coral hover:underline flex-shrink-0 min-h-[44px]" data-id="element-3638">
            <Download size={14} data-id="element-3639" />
            Scarica template
          </button>
        </div>

        {expectedColumns && expectedColumns.length > 0 && <div className="flex items-center gap-2 flex-wrap mt-4" data-id="element-3640">
            <span className="text-[11px] font-medium text-muted-foreground" data-id="element-3641">
              Colonne attese:
            </span>
            {expectedColumns.map(c => <span key={c} className="text-[11px] font-medium text-navy bg-muted rounded-md px-2 py-0.5" data-id="element-3642">
                {c}
              </span>)}
          </div>}
      </div>

      {/* Uploader / confirmation */}
      <div className="p-5 lg:p-6" data-id="element-3643">
        {fileName ? <div className="flex items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3" data-id="element-3644">
            <span className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700 min-w-0" data-id="element-3645">
              <CheckCircle size={16} className="text-emerald-600 flex-shrink-0" data-id="element-3646" />
              <span className="truncate" data-id="element-3647">{fileName}</span>
              <span className="text-emerald-600 hidden sm:inline" data-id="element-3648">
                · pronto all'import
              </span>
            </span>
            {onClear && <button onClick={onClear} aria-label="Rimuovi file" className="text-xs font-medium text-muted-foreground hover:text-foreground flex-shrink-0 min-h-[44px] px-2" data-id="element-3649">
                Rimuovi
              </button>}
          </div> : <label className="block border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-coral hover:bg-coral/5 transition-colors" data-id="element-3650">
            <input type="file" accept=".csv" className="hidden" onChange={onFile} data-id="element-3651" />
            <span className="flex flex-col items-center gap-2" data-id="element-3652">
              <UploadCloud size={24} className="text-muted-foreground" data-id="element-3653" />
              <span className="text-sm font-medium text-navy" data-id="element-3654">
                Trascina o seleziona il file CSV
              </span>
              <span className="text-xs text-muted-foreground" data-id="element-3655">
                Usa il template per un import senza errori. Puoi anche saltare e
                farlo più tardi.
              </span>
            </span>
          </label>}

        {children}
      </div>
    </div>;
}
// Shared bottom navigation (Indietro / Continua)
function StepNav({
  onBack,
  onNext,
  nextDisabled = false,
  nextLabel = 'Continua'
}: {
  onBack: () => void;
  onNext: () => void;
  nextDisabled?: boolean;
  nextLabel?: string;
}) {
  return <div className="flex items-center justify-between pt-4" data-id="element-3656">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors" data-id="element-3657">
        <ArrowLeft size={16} data-id="element-3658" />
        Indietro
      </button>
      <button onClick={onNext} disabled={nextDisabled} className="bg-coral text-white rounded-xl px-6 py-2.5 text-sm font-semibold hover:bg-coral-600 transition-colors shadow-sm min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3659">
        {nextLabel}
      </button>
    </div>;
}